import CoachRosterPage from './pages/CoachRosterPage';
import StringingWorkspace, { StringingRoute } from './features/stringing/StringingWorkspace';
import { Fragment, useEffect, useSyncExternalStore } from 'react';
import { getAccountBoundaryKey, getObservedAccountId, subscribeAccountBoundary } from '../../shared/src/lib/accountBoundary';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@tark/shared/hooks';
import { Loader2 } from 'lucide-react';

// Layouts
import AppLayout from './layouts/AppLayout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import SquadsPage from './pages/SquadsPage';
import SquadDetailPage from './pages/SquadDetailPage';
import SquadPairHistoryPage from './pages/SquadPairHistoryPage';
import SquadLeaderboardPage from './pages/SquadLeaderboardPage';
import HistoricalImportPage from './pages/HistoricalImportPage';
import PlayPage from './pages/PlayPage';
import MatchHistoryDetailPage from './pages/MatchHistoryDetailPage';
import PilotRatingPage from './pages/PilotRatingPage';
import SchedulePage from './pages/SchedulePage';
import CoachTimePollsPage from './pages/CoachTimePollsPage';
import CoachScheduleDraftPage from './pages/CoachScheduleDraftPage';
import CoachClassesPage from './pages/CoachClassesPage';
import CoachLeadsPage from './pages/CoachLeadsPage';
import CoachAttendancePage from './pages/CoachAttendancePage';
import CoachPaymentsPage from './pages/CoachPaymentsPage';
import CoachStudioSettingsPage from './pages/CoachStudioSettingsPage';
import BuzzPage from './pages/BuzzPage';
import ProsPage from './pages/ProsPage';
import ProsProviderPage from './pages/ProsProviderPage';
import BecomeProviderPage from './pages/BecomeProviderPage';
import ProsManagePage from './pages/ProsManagePage';
import ProsBusinessesPage from './pages/ProsBusinessesPage';
import DirectoryPage from './pages/DirectoryPage';
import ProfilePage from './pages/ProfilePage';
import EditProfilePage from './pages/EditProfilePage';
import ChangePhonePage from './pages/ChangePhonePage';
import JoinOrganizationPage from './pages/JoinOrganizationPage';
import TenantAccessPage from './pages/TenantAccessPage';
import FamilyPage from './pages/FamilyPage';
import CoachMatchPlayPage from './pages/CoachMatchPlayPage';
import PublicCompetitionPage from './pages/competitions/PublicCompetitionPage';
import OrganizerCompetitionsPage from './pages/competitions/OrganizerCompetitionsPage';
import LeagueEventStudioPage from './pages/competitions/LeagueEventStudioPage';
import LeaguePilotStudioPage from './pages/competitions/LeaguePilotStudioPage';
import PublicLeagueEventPage from './pages/competitions/PublicLeagueEventPage';
import SquadInvitePage from './pages/SquadInvitePage';
import ReleasesPage from './pages/ReleasesPage';
import ReportProblemPage from './pages/ReportProblemPage';
import MatchClaimLandingPage from './pages/MatchClaimLandingPage';

// ============================================
// Protected Route Wrapper
// ============================================

function AccountViewBoundary({ children }: { children: React.ReactNode }) {
  const key = useSyncExternalStore(subscribeAccountBoundary, getAccountBoundaryKey, getAccountBoundaryKey);
  const { user, isLoading } = useAuth();
  if (isLoading || (user?.id ? String(user.id) : null) !== getObservedAccountId()) {
    return <div role="status" className="min-h-screen bg-background" aria-label="Checking account" />;
  }
  return <Fragment key={key}>{children}</Fragment>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const returnTarget = `${location.pathname}${location.search}`;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/login?return=${encodeURIComponent(returnTarget)}`} replace />;
  }

  if (user?.nextStep && !['TABS', 'WELCOME'].includes(user.nextStep)) {
    return <Navigate to={`/register?return=${encodeURIComponent(returnTarget)}`} replace />;
  }

  return <AccountViewBoundary>{children}</AccountViewBoundary>;
}

function PublicEntryRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.nextStep && !['TABS', 'WELCOME'].includes(user.nextStep)) {
      return <Navigate to="/register" replace />;
    }

    return <Navigate to="/welcome" replace />;
  }

  return <HomePage />;
}

function StaticPageRedirect({ target }: { target: string }) {
  useEffect(() => {
    window.location.replace(target);
  }, [target]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" />
  );
}

// ============================================
// App Component
// ============================================

export default function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicEntryRoute />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<AccountViewBoundary><RegisterPage /></AccountViewBoundary>} />
      <Route path="/releases" element={<ReleasesPage />} />
      <Route path="/join/:codeOrToken" element={<SquadInvitePage />} />
      <Route path="/match-claim/:token" element={<MatchClaimLandingPage />} />
      <Route path="/competitions/:slug" element={<PublicCompetitionPage />} />
      <Route path="/leagues/:slug" element={<PublicLeagueEventPage />} />
      <Route path="/terms" element={<StaticPageRedirect target="/terms.html" />} />
      <Route path="/privacy" element={<StaticPageRedirect target="/privacy.html" />} />
      <Route path="/account/delete" element={<StaticPageRedirect target="/account-delete.html" />} />
      <Route path="/data-request" element={<StaticPageRedirect target="/data-request.html" />} />
      <Route path="/accessibility" element={<StaticPageRedirect target="/accessibility.html" />} />
      <Route path="/sms-opt-in" element={<StaticPageRedirect target="/sms-opt-in.html" />} />
      <Route path="/copyright-policy" element={<StaticPageRedirect target="/copyright-policy.html" />} />
      <Route path="/pros-terms" element={<StaticPageRedirect target="/pros-terms.html" />} />
      <Route path="/alampana" element={<StaticPageRedirect target="/alampana.html" />} />

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/home" element={<Navigate to="/welcome" replace />} />
        <Route path="/welcome" element={<StringingRoute view="today"><WelcomePage /></StringingRoute>} />
        <Route path="/stringing/*" element={<StringingWorkspace />} />

        {/* Squads - Nested routes for master-detail layout */}
        <Route path="/squads/:squadId/import-history" element={<HistoricalImportPage />} />
        <Route path="/squads" element={<SquadsPage />}>
          <Route path=":squadId" element={<SquadDetailPage />} />
        </Route>

        {/* Play */}
        <Route path="/play" element={<PlayPage />} />
        <Route path="/squads/:id/leaderboard" element={<SquadLeaderboardPage />} />
        <Route path="/squads/:id/leaderboard/pairs/:first/:second" element={<SquadPairHistoryPage />} />
        <Route path="/play/matches/:matchId" element={<MatchHistoryDetailPage />} />
        <Route path="/play/rating" element={<PilotRatingPage />} />

        {/* Schedule */}
        <Route path="/classes" element={<CoachClassesPage />} />
        <Route path="/leads" element={<CoachLeadsPage />} />
        <Route path="/attendance" element={<CoachAttendancePage />} />
        <Route path="/payments" element={<CoachPaymentsPage />} />
        <Route path="/studio/settings" element={<CoachStudioSettingsPage />} />
        <Route path="/schedule" element={<StringingRoute view="orders"><SchedulePage /></StringingRoute>} />
              <Route path="/schedule/roster/:occurrenceId" element={<CoachRosterPage />} />
        <Route path="/schedule/time-polls" element={<CoachTimePollsPage />} />
        <Route path="/schedule/drafts/:draftId" element={<CoachScheduleDraftPage />} />
        <Route path="/match-play" element={<CoachMatchPlayPage />} />
        <Route path="/organizer/competitions" element={<OrganizerCompetitionsPage />} />
        <Route path="/organizer/leagues" element={<LeagueEventStudioPage />} />
        <Route path="/organizer/leagues/:id" element={<LeagueEventStudioPage />} />
        <Route path="/organizer/leagues/:id/play" element={<LeaguePilotStudioPage />} />

        {/* Shared content / marketplace shells */}
        <Route path="/buzz" element={<BuzzPage />} />
        <Route path="/pros" element={<ProsPage />} />
        <Route path="/pros/provider/:providerId" element={<ProsProviderPage />} />
        <Route path="/pros/become" element={<BecomeProviderPage />} />
        <Route path="/pros/manage" element={<ProsManagePage />} />
        <Route path="/pros/businesses" element={<ProsBusinessesPage />} />
        <Route path="/directory" element={<StringingRoute view="clients"><DirectoryPage /></StringingRoute>} />
        <Route path="/academy" element={<Navigate to="/pros" replace />} />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/edit" element={<EditProfilePage />} />
        <Route path="/profile/change-phone" element={<ChangePhonePage />} />
        <Route path="/family" element={<FamilyPage />} />
        <Route path="/join-organization" element={<JoinOrganizationPage />} />
        <Route path="/tenant-access" element={<TenantAccessPage />} />
        <Route path="/report-a-problem" element={<ReportProblemPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/welcome" replace />} />
      </Route>
    </Routes>
  );
}
